import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, BriefcaseIcon, ClockIcon, LandmarkIcon } from 'lucide-react';
import { PropertyContextCard } from '../components/shell/PropertyContextCard';
import { ComplianceFooter } from '../components/shell/ComplianceFooter';
import { getPropertyContext } from '../utils/propertyContext';
import { PortalHeader } from '../components/shell/PortalHeader';
import { LoanOfficerChip, LoanOfficerNote } from '../components/shell/LoanOfficerChip';
import { JourneyStages } from '../components/shell/JourneyStages';
import { SaveIndicator } from '../components/ui/SaveIndicator';
import { Button } from '../components/ui/Button';
import { usePortal } from '../contexts/PortalContext';
import { groupForStep, minutesLeft, progressPercent } from '../utils/applicationFlow';
const ease = [0.23, 1, 0.32, 1] as const;
export function Welcome() {
  const navigate = useNavigate();
  const {
    answers,
    completed,
    lastStep
  } = usePortal();
  const pct = progressPercent(completed, answers);
  const mins = minutesLeft(completed, answers);
  const group = groupForStep(lastStep, answers);
  const resume = () => navigate(`/apply/${lastStep}`);
  const property = getPropertyContext(answers);
  const goalLine = property.variant === 'refinance' ? 'Let’s finish your refinance application.' : property.variant === 'shopping' ? 'Let’s finish your pre-approval application.' : 'Let’s finish your mortgage application.';
  return <div className="min-h-screen w-full bg-canvas pb-28 md:pb-0" data-id="element-1114" data-mp="3wzs3kl3wp5t">
      <PortalHeader right={<LoanOfficerChip data-id="element-1116" data-mp="8urho7ya3z6u" />} data-id="element-1115" data-mp="8vb15t3kuvmz" />

      <main className="mx-auto grid max-w-6xl gap-10 px-5 pt-8 md:px-8 md:pt-16 lg:grid-cols-12 lg:gap-16" data-id="element-1117" data-mp="8tgiklfs3fm2">
        <section className="lg:col-span-7" aria-labelledby="welcome-title" data-id="element-1118" data-mp="dn2jxhghrx2k">
          <motion.p initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease
        }} className="text-[15px] text-ink-500" data-id="element-1119" data-mp="3cayouofwkekd">
            Good afternoon
          </motion.p>
          <motion.h1 id="welcome-title" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease,
          delay: 0.05
        }} className="mt-1 text-[40px] font-semibold leading-[1.05] tracking-[-0.035em] text-ink-900 md:text-[64px]" data-id="element-1120" data-mp="398lseniml329">
            Welcome back, {answers.firstName}.
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease,
          delay: 0.1
        }} className="mt-4 max-w-md text-lg text-ink-500 md:text-xl" data-id="element-1121" data-mp="2qbsyqq5jq07e">
            {goalLine} You’re picking up at <span className="text-ink-900" data-id="element-1122" data-mp="307dva8rui89r">{group?.label}</span>.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease,
          delay: 0.15
        }} className="mt-10 max-w-lg" data-id="element-1123" data-mp="2v92jhshzpnow">
            <div className="flex items-end justify-between" data-id="element-1124" data-mp="aky51lqa8f1z">
              <div data-id="element-1125" data-mp="1shro8gmjvxeh">
                <span className="tabular text-5xl font-semibold tracking-tight text-ink-900" data-id="element-1126" data-mp="1sh9w9w6x85cd">{pct}%</span>
                <span className="ml-2 text-ink-500" data-id="element-1127" data-mp="1xh4lzweymovu">of your application</span>
              </div>
              <span className="flex items-center gap-1.5 pb-1.5 text-sm text-ink-500" data-id="element-1128" data-mp="1jhmv9bi3t1ju">
                <ClockIcon className="h-4 w-4" aria-hidden data-id="element-1129" data-mp="1ofwm59wspfzn" />
                About {mins} min left
              </span>
            </div>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-ink-200" data-id="element-1130" data-mp="2c721lnytq69f">
              <motion.div className="h-full rounded-full bg-pulse-600" initial={{
              width: 0
            }} animate={{
              width: `${pct}%`
            }} transition={{
              duration: 1,
              ease,
              delay: 0.3
            }} data-id="element-1131" data-mp="2g7m1gzas7nz9" />
            </div>

            <div className="mt-8 hidden items-center gap-5 md:flex" data-id="element-1132" data-mp="27eit35ezu1bh">
              <Button size="lg" onClick={resume} trailingIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-1134" data-mp="2v3le8n8u6caq" />} data-id="element-1133" data-mp="27cpkvloqf91d">
                Resume application
              </Button>
              <SaveIndicator data-id="element-1135" data-mp="2zwwfm8uvq0v9" />
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4,
          delay: 0.25
        }} className="mt-14 max-w-lg" data-id="element-1136" data-mp="1b9sd6kjmrsxa">
            <h2 className="text-sm font-medium text-ink-900" data-id="element-1137" data-mp="16cjumiuklmvi">Your path to closing</h2>
            <div className="mt-4" data-id="element-1138" data-mp="129dorcbddxpk">
              <JourneyStages current={0} currentProgress={pct} data-id="element-1139" data-mp="x9r3cfle1b5o" />
            </div>
            <p className="mt-4 text-sm leading-relaxed text-ink-500" data-id="element-1140" data-mp="xbu4nvrzqqpp">
              Next you’ll authorize a credit check, choose a loan option, and submit your application. We handle the lender, appraisal, and title work behind the scenes.
            </p>
          </motion.div>
        </section>

        <motion.aside initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease,
        delay: 0.2
      }} className="lg:col-span-5" data-id="element-1141" data-mp="1l5lm7ty554ab">
          <PropertyContextCard context={property} data-id="element-1142" data-mp="2dp2c44sjcyqw">
            <LoanOfficerNote message="I’ve already filled in what you shared on our call. You’ll just confirm a few things and add your finances." data-id="element-1143" data-mp="28xalxvdakv1a" />
          </PropertyContextCard>

          <div className="mt-6 px-1" data-id="element-1144" data-mp="2gajm6iy2i91q">
            <h2 className="text-sm font-medium text-ink-900" data-id="element-1145" data-mp="2l707z6hsobbn">Have these handy</h2>
            <ul className="mt-3 space-y-2.5 text-sm text-ink-500" data-id="element-1146" data-mp="2q6mnxnp0c5o5">
              <li className="flex items-center gap-3" data-id="element-1147" data-mp="2q5bk5nvkaxx1">
                <BriefcaseIcon className="h-4 w-4 text-ink-400" aria-hidden data-id="element-1148" data-mp="2u8gkpkw30pj1" /> Employer names and start dates for the last 2 years
              </li>
              <li className="flex items-center gap-3" data-id="element-1149" data-mp="2z56z83s8nd2c">
                <LandmarkIcon className="h-4 w-4 text-ink-400" aria-hidden data-id="element-1150" data-mp="345ma8op1s8h5" /> Approximate balances for your bank and retirement accounts
              </li>
            </ul>
          </div>
        </motion.aside>
      </main>
      <ComplianceFooter className="mt-10" data-id="element-1151" data-mp="3duw5gdf6u79h" />

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-ink-200 bg-white/95 px-5 pb-6 pt-3 backdrop-blur md:hidden" data-id="element-1152" data-mp="3hxsryf7umnip">
        <Button size="lg" block onClick={resume} trailingIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-1154" data-mp="3mustqdxo1440" />} data-id="element-1153" data-mp="3mw426r22301g">
          Resume application
        </Button>
        <div className="mt-2 flex justify-center" data-id="element-1155" data-mp="1msjnn1duplqc">
          <SaveIndicator data-id="element-1156" data-mp="1itu38oueb3x7" />
        </div>
      </div>
    </div>;
}